import React, { useEffect, useRef, useState, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import apiService, { getImageUrl } from "../../services/api";

const CategoryOne = () => {
  const swiperRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await apiService.getCategories();
        
        // Map API data to component format
        const mappedCategories = response.results?.map(category => ({
          id: category.id,
          title: category.name,
          slug: category.slug,
          description: category.description,
          imgSrc: category.image ? getImageUrl(category.image) : `/assets/img/category/category_1_${(category.id % 5) + 1}.jpg`,
        })) || [];
        
        console.log('CategoryOne: Loaded categories:', mappedCategories.length);
        setCategories(mappedCategories);
        setError(null);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
        // Fallback to default categories if API fails
        setCategories([
          { id: 1, title: "Adventure Tourism", imgSrc: "/assets/img/category/category_1_1.jpg", slug: "adventure-tourism" },
          { id: 2, title: "Cultural Tourism", imgSrc: "/assets/img/category/category_1_2.jpg", slug: "cultural-tourism" },
          { id: 3, title: "Beach Tourism", imgSrc: "/assets/img/category/category_1_3.jpg", slug: "beach-tourism" },
          { id: 4, title: "Wildlife Tourism", imgSrc: "/assets/img/category/category_1_4.jpg", slug: "wildlife-tourism" },
          { id: 5, title: "Mountain Tourism", imgSrc: "/assets/img/category/category_1_5.jpg", slug: "mountain-tourism" }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Calculate display categories and loop configuration
  const swiperConfig = useMemo(() => {
    const slideCount = categories.length;
    const minSlidesForLoop = 8; // Minimum slides for smooth loop
    const shouldEnableLoop = slideCount >= minSlidesForLoop;
    
    // If we have fewer slides than needed for loop, duplicate them
    let slidesToShow = categories;
    if (!shouldEnableLoop && slideCount > 0 && slideCount < minSlidesForLoop) {
      const duplicatesNeeded = Math.ceil(minSlidesForLoop / slideCount);
      slidesToShow = Array(duplicatesNeeded).fill(categories).flat();
    }

    console.log(`CategoryOne: Rendering with ${slidesToShow.length} categories, loop: ${shouldEnableLoop || slidesToShow.length >= minSlidesForLoop}`);
    
    return {
      slides: slidesToShow,
      loop: shouldEnableLoop || slidesToShow.length >= minSlidesForLoop,
    };
  }, [categories]);

  useEffect(() => {
    if (!swiperRef.current) return;

    const swiperInstance = swiperRef.current.swiper;

    // ✅ Start autoplay properly
    if (swiperInstance && swiperInstance.autoplay) {
      swiperInstance.autoplay.start();
    }
    // ✅ Custom pagination with numbers
    if (swiperInstance.pagination) {
      swiperInstance.pagination.renderBullet = function (index, className) {
        let formattedNumber = index + 1 < 10 ? "0" + (index + 1) : index + 1;
        return `<span class="${className} number">${formattedNumber}</span>`;
      };
      swiperInstance.pagination.init();
      swiperInstance.pagination.update();
    }
    
    // ✅ Custom wheel effect for category slider (THE ROTATING EFFECT!)
    const multiplier = {
      translate: 0.1,
      rotate: 0.01,
    };

    const calculateWheel = () => {
      const slides = document.querySelectorAll(".single");
      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const r = window.innerWidth * 0.5 - (rect.x + rect.width * 0.5);
        let ty = Math.abs(r) * multiplier.translate - rect.width * multiplier.translate;

        if (ty < 0) {
          ty = 0;
        }
        const transformOrigin = r < 0 ? "left top" : "right top";
        slide.style.transform = `translate(0, ${ty}px) rotate(${-r * multiplier.rotate}deg)`;
        slide.style.transformOrigin = transformOrigin;
      });
    };

    const raf = () => {
      requestAnimationFrame(raf);
      calculateWheel();
    };

    raf();

    return () => cancelAnimationFrame(raf);
  }, [categories]); // Re-run when categories change

  return (
    <section
      className="category-area bg-top-center"
      style={{ backgroundImage: "url(/assets/img/bg/category_bg_1.png)", backgroundRepeat: "no-repeat" }}
    >
      <div className="container th-container">
        <div className="title-area text-center">
          <span className="sub-title">Wonderful Place For You</span>
          <h2 className="sec-title">Tour Categories</h2>
        </div>

        <Swiper
          ref={swiperRef}
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          breakpoints={{
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            992: { slidesPerView: 3 },
            1200: { slidesPerView: 3 },
            1400: { slidesPerView: 5 },
          }}
          spaceBetween={40}
          loop={swiperConfig.loop} // Intelligent loop based on slide count
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          pagination={{
            clickable: true,
            el: ".swiper-pagination",
            type: "bullets"
          }}
          className="th-slider has-shadow categorySlider"
        >
          {loading ? (
            // Loading state - show placeholder slides
            [...Array(5)].map((_, index) => (
              <SwiperSlide key={`loading-${index}`}>
                <div className="category-card single">
                  <div className="box-img global-img">
                    <div style={{height: '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      Loading...
                    </div>
                  </div>
                  <h3 className="box-title">
                    <span>Loading...</span>
                  </h3>
                </div>
              </SwiperSlide>
            ))
          ) : swiperConfig.slides.length > 0 ? (
            swiperConfig.slides.map((category, index) => (
              <SwiperSlide key={`${category.id}-${index}`}>
                <div className="category-card single">
                  <div className="box-img global-img">
                    <img src={category.imgSrc} alt={category.title} loading="lazy" />
                  </div>
                  <h3 className="box-title">
                    <Link to={`/categories/${category.slug}`}>{category.title}</Link>
                  </h3>
                  <Link className="line-btn" to={`/categories/${category.slug}`}>
                    See More
                  </Link>
                </div>
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div className="category-card single">
                <div className="box-img global-img">
                  <div style={{height: '200px', backgroundColor: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    {error || 'No categories available'}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          )}
          
          <div className="slider-controller w-100 justify-content-center">
            <div className="swiper-pagination" style={{maxWidth:"100%"}}></div>
          </div>
        </Swiper>

      </div>
    </section>
  );
};

export default CategoryOne;
